import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Label, Input, Button, Spinner } from "reactstrap";
import { motion, useReducedMotion } from "framer-motion";

import "./xavaAuth.scss";
import { useState } from "react";
import { useLoginAuthMutation } from "../../service";
import { Controller, useForm } from "react-hook-form";
import { setUserInfo, setUserToken } from "../../redux/authSlice";
import Cookies from "universal-cookie";
import { useDispatch } from "react-redux";
import { requestWelcomeGreeting } from "../common/WelcomeOverlay";
import TechBackdrop from "../common/TechBackdrop";
import xavaLogo from "../../assets/images/xava-logo.png";

const cookies = new Cookies();

function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const reduceMotion = useReducedMotion();
  const [errorMessage, setErrorMessage] = useState("");
  const [passwordShow, setPasswordShow] = useState(false);
  const [loginReq, loginRes] = useLoginAuthMutation();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const handleLogin = (state) => {
    const reqData = {
      email: state?.email,
      password: state?.password,
    };
    loginReq(reqData);
  };

  useEffect(() => {
    if (loginRes?.isSuccess) {
      const userData = loginRes?.data?.data;
      cookies.set("MMCL", userData?.token);
      cookies.set("MMCL_user", userData);
      dispatch(setUserToken(userData?.token));
      dispatch(setUserInfo(userData));
      setErrorMessage("");
      requestWelcomeGreeting();

      if (userData?.otpSkipped) {
        if (userData.role === "admin" || userData.role === "staff") {
          navigate("/dashboard");
        } else if (userData.role === "employee") {
          if (userData.hasSubmittedDetails) {
            navigate("/EmpAttendance");
          } else {
            navigate("/employee-form");
          }
        } else if (userData.role === "contractor") {
          navigate("/contractordetails");
        }
      } else {
        navigate("/otp", {
          state: {
            fromLogin: true,
            email: userData?.email,
            userData,
          },
        });
      }
    } else if (loginRes?.isError) {
      const errorData = loginRes?.error?.data;
      const message = errorData?.message || "Invalid credential";
      setErrorMessage(message);
    }
  }, [loginRes?.isSuccess, loginRes?.isError]);

  const rise = (delay) =>
    reduceMotion
      ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.2 } }
      : {
          initial: { opacity: 0, y: 14 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] },
        };

  return (
    <div className="xv-auth">
      <div className="xv-auth-brand">
        <div className="xv-auth-aurora" aria-hidden="true">
          <span className="a" />
          <span className="b" />
        </div>
        <TechBackdrop variant="brand" />

        <motion.div className="xv-auth-brand-top" {...rise(0.05)}>
          <span className="xv-auth-logo-chip">
            <img src={xavaLogo} alt="Xava Web Services" />
          </span>
        </motion.div>

        <motion.div className="xv-auth-brand-copy" {...rise(0.2)}>
          <h1>Your workday starts here.</h1>
          <p>
            Check in for the day, apply for leave and keep an eye on upcoming
            holidays. Everything you need, in one place.
          </p>
        </motion.div>

        <motion.div className="xv-auth-brand-foot" {...rise(0.35)}>
          © {new Date().getFullYear()} Xava Web Services Pvt. Ltd.
        </motion.div>
      </div>

      <div className="xv-auth-form-wrap">
        <motion.form
          className="xv-auth-form"
          onSubmit={handleSubmit(handleLogin)}
          {...rise(0.1)}
        >
          <div className="xv-auth-form-logo">
            <img src={xavaLogo} alt="Xava Web Services" />
          </div>

          <h2>Welcome back</h2>
          <p className="xv-auth-sub">Sign in to your Xava CRM account</p>

          {errorMessage && (
            <span className="xv-auth-error" role="alert">
              {errorMessage}
            </span>
          )}

          <div className="mb-2">
            <Label className="form-label" for="login-email">
              Email
            </Label>
            <Controller
              id="email"
              name="email"
              control={control}
              rules={{
                required: "Email is required",
                validate: {
                  matchPattern: (v) =>
                    /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g.test(v) ||
                    "Email address must be a valid address",
                },
              }}
              render={({ field }) => (
                <Input
                  type="email"
                  id="login-email"
                  placeholder="you@xava.co.in"
                  autoComplete="email"
                  invalid={!!errors?.email}
                  {...field}
                />
              )}
            />
            {errors?.email && (
              <span className="xv-field-error">{errors?.email?.message}</span>
            )}
          </div>

          <div className="mb-2">
            <Label className="form-label" for="login-password">
              Password
            </Label>
            <div className="input-group-merge input-group">
              <Controller
                id="password"
                name="password"
                control={control}
                rules={{
                  required: "Password is required",
                }}
                render={({ field }) => (
                  <Input
                    placeholder="Enter your password"
                    id="login-password"
                    type={passwordShow ? "text" : "password"}
                    className="form-control"
                    autoComplete="current-password"
                    invalid={!!errors?.password}
                    {...field}
                  />
                )}
              />
              <span
                className="cursor-pointer input-group-text"
                role="button"
                tabIndex={0}
                aria-label={passwordShow ? "Hide password" : "Show password"}
                onClick={() => setPasswordShow(!passwordShow)}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") &&
                  setPasswordShow(!passwordShow)
                }
              >
                {passwordShow ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                )}
              </span>
            </div>
            {errors?.password && (
              <span className="xv-field-error">
                {errors?.password?.message}
              </span>
            )}
          </div>

          <Button
            color="primary"
            block
            className="mt-2"
            disabled={loginRes.isLoading}
          >
            {loginRes.isLoading ? <Spinner size="sm" /> : "Sign in"}
          </Button>

          <p className="xv-auth-links">
            <Link to="/Forgetpassword">Forgot password?</Link>
          </p>
        </motion.form>
      </div>
    </div>
  );
}

export default Login;
